import { useState, useRef, useEffect } from 'react';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import { Mic, MicOff, Volume2, VolumeX, Square } from 'lucide-react';
import { toast } from 'sonner';

export default function VoicePage() {
  const [listening, setListening] = useState(false);
  const [transcript, setTranscript] = useState('');
  const [speaking, setSpeaking] = useState(false);
  const [textToSpeak, setTextToSpeak] = useState('');
  const recognitionRef = useRef(null);

  const toggleSpeechToText = () => {
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SR) { toast.error('Speech recognition not supported'); return; }
    if (listening) {
      recognitionRef.current?.stop();
      setListening(false);
      return;
    }
    const recognition = new SR();
    recognition.continuous = true;
    recognition.interimResults = true;
    recognition.lang = 'en-US';
    recognition.onresult = (e) => {
      let result = '';
      for (let i = 0; i < e.results.length; i++) result += e.results[i][0].transcript;
      setTranscript(result);
    };
    recognition.onerror = () => { setListening(false); toast.error('Speech recognition error'); };
    recognition.onend = () => setListening(false);
    recognition.start();
    recognitionRef.current = recognition;
    setListening(true);
  };

  const speakText = () => {
    const text = textToSpeak.trim() || transcript.trim();
    if (!text) { toast.error('No text to speak'); return; }
    if (!window.speechSynthesis) { toast.error('Text-to-speech not supported'); return; }
    if (speaking) { window.speechSynthesis.cancel(); setSpeaking(false); return; }
    const utterance = new SpeechSynthesisUtterance(text);
    utterance.rate = 0.9;
    utterance.pitch = 1;
    utterance.onend = () => setSpeaking(false);
    utterance.onerror = () => setSpeaking(false);
    window.speechSynthesis.speak(utterance);
    setSpeaking(true);
  };

  useEffect(() => {
    return () => {
      recognitionRef.current?.stop();
      window.speechSynthesis?.cancel();
    };
  }, []);

  return (
    <div className="max-w-2xl mx-auto animate-in" data-testid="voice-page">
      <h1 className="font-serif text-2xl md:text-3xl mb-2">Voice Studio</h1>
      <p className="text-sm text-muted-foreground mb-8">Speak to text or listen to content read aloud</p>

      {/* Speech to Text */}
      <div className="border border-border/40 p-6 mb-8">
        <h2 className="font-serif text-lg mb-4">Speech to Text</h2>
        <div className="flex justify-center mb-6">
          <button
            data-testid="stt-toggle-btn"
            onClick={toggleSpeechToText}
            className={`w-20 h-20 rounded-full flex items-center justify-center transition-all duration-300 ${
              listening
                ? 'bg-gold text-obsidian voice-active shadow-lg shadow-gold/30'
                : 'bg-obsidian text-white hover:bg-gold hover:text-obsidian'
            }`}
          >
            {listening ? <MicOff size={28} /> : <Mic size={28} />}
          </button>
        </div>
        <p className="text-center text-xs text-muted-foreground mb-4">
          {listening ? 'Listening... speak now' : 'Tap the microphone to start'}
        </p>
        {/* Waveform visualization */}
        {listening && (
          <div className="flex items-center justify-center gap-1 h-8 mb-4" data-testid="voice-waveform">
            {[...Array(12)].map((_, i) => (
              <div
                key={i}
                className="w-1 bg-gold rounded-full animate-pulse"
                style={{
                  height: `${Math.random() * 24 + 8}px`,
                  animationDelay: `${i * 0.1}s`,
                  animationDuration: `${0.5 + Math.random() * 0.5}s`
                }}
              />
            ))}
          </div>
        )}
        {transcript && (
          <div className="border border-border/20 p-4 bg-mist" data-testid="stt-result">
            <p className="text-sm font-sans leading-relaxed">{transcript}</p>
          </div>
        )}
      </div>

      {/* Text to Speech */}
      <div className="border border-border/40 p-6">
        <h2 className="font-serif text-lg mb-4">Text to Speech</h2>
        <Textarea
          data-testid="tts-input"
          placeholder="Type or paste text to be read aloud..."
          value={textToSpeak}
          onChange={e => setTextToSpeak(e.target.value)}
          className="rounded-none border-0 border-b border-input bg-transparent px-0 py-2 shadow-none focus-visible:ring-0 resize-none min-h-[80px] text-sm mb-4"
        />
        <div className="flex gap-3">
          <Button
            data-testid="tts-speak-btn"
            onClick={speakText}
            className={`rounded-sm text-xs uppercase tracking-wider h-10 px-6 transition-colors duration-300 ${
              speaking
                ? 'bg-gold text-obsidian hover:bg-obsidian hover:text-white'
                : 'bg-obsidian hover:bg-gold hover:text-obsidian'
            }`}
          >
            {speaking ? <><Square size={12} className="mr-2" /> Stop</> : <><Volume2 size={12} className="mr-2" /> Read Aloud</>}
          </Button>
          {transcript && !textToSpeak && (
            <Button data-testid="tts-use-transcript" variant="outline" size="sm" className="rounded-sm text-xs" onClick={() => setTextToSpeak(transcript)}>
              Use Transcript
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}
